/**
 * The editor extensions the hosted apps bring (I-2799): the ERP's text blocks
 * and whatever follows. An app entry names a small public module of its own
 * (`editorExtensions`); the shell loads it at startup — never the whole app —
 * and hands what it returns to every editor of every app. An app's
 * extensions only show while its module is reachable and the account may
 * open the app: Mail offers text blocks only while the ERP answers.
 */
import { computed, shallowRef, type ComputedRef } from 'vue';
import type { NxRichTextExtension } from '@blpde/nx-ui';
import { apps, type AppEntry } from '@/integration';

/** What an app's extension module exports: its extensions in a language. */
export type EditorExtensionFactory = (locale: string) => readonly NxRichTextExtension[];

const factories = shallowRef<ReadonlyMap<string, EditorExtensionFactory>>(new Map());
let requested = false;

function loadFactories(): void {
  if (requested) return;
  requested = true;
  for (const entry of apps) {
    if (!entry.editorExtensions) continue;
    entry
      .editorExtensions()
      .then((module) => {
        factories.value = new Map(factories.value).set(entry.id, module.editorExtensions);
      })
      // An app whose bundle cannot be loaded brings no extensions; the editors work on.
      .catch(() => undefined);
  }
}

export interface EditorExtensionsOptions {
  /** Whether the account may use the app now: entitled, its module online, allowed. */
  usable: (entry: AppEntry) => boolean;
  locale: () => string;
}

/** The extensions of the apps that are usable now, in the shell's language. */
export function useEditorExtensions(
  options: EditorExtensionsOptions,
): ComputedRef<readonly NxRichTextExtension[]> {
  loadFactories();
  return computed(() =>
    apps.flatMap((entry) => {
      const factory = factories.value.get(entry.id);
      return factory && options.usable(entry) ? factory(options.locale()) : [];
    }),
  );
}
